/**
 * Translated version of friendlyTimeFormatter.
 *
 * This wraps the i18n helpers to produce localized, pluralized human time strings
 * with the same behavior and fallbacks as the original friendlyTimeFormatter.
 */
import { formatTimeWithSuffix, translateHumanTime, ReactI18nextTranslationFunction } from './coreTranslationHelpers';
import { getTimestamp, TIMES } from '../timeUtil';
import { mapLocalePreferenceToIntlLocale } from '../datetime/friendlyDateFormatterIntl';
import { FRIENDLY_TIME_ABSOLUTE_DATE_OPTIONS } from '../datetime/intlDateFormatPresets';

export default ({
    t,
    timestamp,
    fromTimestamp = getTimestamp(),
    lessThanMinuteMessage,
    showSuffix,
    locale,
}: {
    t: ReactI18nextTranslationFunction;
    timestamp: number;
    fromTimestamp?: number;
    lessThanMinuteMessage?: string;
    showSuffix?: boolean;
    // App locale preference (e.g. en-au, es-es). Normalized before Intl use.
    locale?: string;
}): string => {
    const millisPassed = fromTimestamp - timestamp;

    // If less than a minute just show this string. If a custom message is
    // provided prefer it; otherwise fall back to translateHumanTime's justNow.
    if (millisPassed > 0 && millisPassed < TIMES.MINUTE) {
        if (lessThanMinuteMessage) return lessThanMinuteMessage;
        return translateHumanTime(t, 0);
    }

    // Keep relative strings for recent dates, but switch older dates to a concrete
    // locale-native calendar date using the shared Intl preset.
    if (Math.abs(millisPassed) > 4 * TIMES.WEEK) {
        const intlLocale = mapLocalePreferenceToIntlLocale(locale);
        const date = new Intl.DateTimeFormat(intlLocale, FRIENDLY_TIME_ABSOLUTE_DATE_OPTIONS).format(
            new Date(timestamp),
        );

        return t('time.templates.absoluteDate', { date, defaultValue: date });
    }

    // Use the i18n helpers which return translated, pluralized time and optional suffix
    if (typeof showSuffix === 'undefined') showSuffix = true;

    // formatTimeWithSuffix uses translateHumanTime internally and will add suffixes
    return formatTimeWithSuffix(t, millisPassed / 1000, showSuffix);
};
